import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Button,
    Grid,
    Card,
    CardContent,
    TextField,
    TextareaAutosize,
    AppBar,
    Toolbar,
    IconButton,
    useTheme,
    useMediaQuery
} from '@mui/material';
import {
    Phone as PhoneIcon,
    Email as EmailIcon,
    LocationOn as LocationIcon,
    Check as CheckIcon,
    Menu as MenuIcon
} from '@mui/icons-material';
import Typewriter from 'typewriter-effect';
import logo from './axentric_logo.png';
import HealthcarePage from './HealthcarePage';
import './App.css';

function MainContent() {
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        email: '',
        company: '',
        message: ''
    });

    const [mobileOpen, setMobileOpen] = useState(false);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        // Handle form submission here
        console.log('Form submitted:', formData);
        // You can add your form submission logic here
    };

    return (
        <Box sx={{ 
            flexGrow: 1, 
            backgroundColor: '#050505', 
            minHeight: '100vh', 
            position: 'relative', 
            overflow: 'hidden',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            '&::before': {
                content: '""',
                position: 'absolute',
                top: '0',
                right: '0',
                width: '400px',
                height: '400px',
                background: 'radial-gradient(circle, rgba(59, 130, 246, 0.02) 0%, rgba(139, 92, 246, 0.01) 50%, transparent 100%)',
                borderRadius: '50%',
                filter: 'blur(80px)',
                zIndex: 0,
                display: { xs: 'none', xl: 'block' }
            }
        }}>
                        {/* Abstract Blue & Purple Lines - Header Area */}
            <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: 0
            }}>
                {/* Top left curved line */}
                <svg
                    width="200"
                    height="200"
                    viewBox="0 0 200 200"
                    style={{
                        position: 'absolute',
                        top: { xs: '15px', md: '20px' },
                        left: { xs: '10px', md: '20px' },
                        opacity: 0.4,
                        width: { xs: '120px', sm: '150px', md: '200px' },
                        height: { xs: '120px', sm: '150px', md: '200px' }
                    }}
                >
                    <path
                        d="M 0 100 Q 50 30 100 100 T 200 100"
                        stroke="url(#bluePurple1)"
                        strokeWidth="0.8"
                        fill="none"
                        strokeLinecap="round"
                        strokeDasharray="1,3"
                    />
                    <path
                        d="M 0 100 Q 50 30 100 100 T 200 100"
                        stroke="url(#bluePurple1)"
                        strokeWidth="3"
                        fill="none"
                        strokeLinecap="round"
                        opacity="0.7"
                    />
                    <defs>
                        <linearGradient id="bluePurple1" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#1e3a8a" />
                            <stop offset="25%" stopColor="#3b82f6" />
                            <stop offset="50%" stopColor="#8b5cf6" />
                            <stop offset="75%" stopColor="#a855f7" />
                            <stop offset="100%" stopColor="#7c3aed" />
                        </linearGradient>
                    </defs>
                </svg>

                {/* Top right curved line */}
                <svg
                    width="250"
                    height="150"
                    viewBox="0 0 250 150"
                    style={{
                        position: 'absolute',
                        top: { xs: '30px', md: '40px' },
                        right: { xs: '15px', md: '30px' },
                        opacity: 0.35,
                        width: { xs: '150px', sm: '200px', md: '250px' },
                        height: { xs: '90px', sm: '120px', md: '150px' }
                    }}
                >
                    <path
                        d="M 0 75 Q 62.5 15 125 75 T 250 75"
                        stroke="url(#bluePurple2)"
                        strokeWidth="0.6"
                        fill="none"
                        strokeLinecap="round"
                    />
                    <path
                        d="M 0 75 Q 62.5 15 125 75 T 250 75"
                        stroke="url(#bluePurple2)"
                        strokeWidth="2.5"
                        fill="none"
                        strokeLinecap="round"
                        opacity="0.6"
                    />
                    <defs>
                        <linearGradient id="bluePurple2" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#1e40af" />
                            <stop offset="33%" stopColor="#6366f1" />
                            <stop offset="66%" stopColor="#a855f7" />
                            <stop offset="100%" stopColor="#7c2d12" />
                        </linearGradient>
                    </defs>
                </svg>

                {/* Center top curved line */}
                <svg
                    width="300"
                    height="120"
                    viewBox="0 0 300 120"
                    style={{
                        position: 'absolute',
                        top: { xs: '60px', md: '80px' },
                        left: '50%',
                        transform: 'translateX(-50%)',
                        opacity: 0.3,
                        width: { xs: '180px', sm: '240px', md: '300px' },
                        height: { xs: '72px', sm: '96px', md: '120px' }
                    }}
                >
                    <path
                        d="M 0 60 Q 75 10 150 60 T 300 60"
                        stroke="url(#bluePurple3)"
                        strokeWidth="1"
                        fill="none"
                        strokeLinecap="round"
                        strokeDasharray="2,4"
                    />
                    <path
                        d="M 0 60 Q 75 10 150 60 T 300 60"
                        stroke="url(#bluePurple3)"
                        strokeWidth="2.8"
                        fill="none"
                        strokeLinecap="round"
                        opacity="0.5"
                    />
                    <defs>
                        <linearGradient id="bluePurple3" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#1d4ed8" />
                            <stop offset="50%" stopColor="#7c3aed" />
                            <stop offset="100%" stopColor="#be185d" />
                        </linearGradient>
                    </defs>
                </svg>

                {/* Additional playful curved lines */}
                <svg
                    width="180"
                    height="100"
                    viewBox="0 0 180 100"
                    style={{
                        position: 'absolute',
                        top: { xs: '90px', md: '120px' },
                        left: { xs: '30px', md: '60px' },
                        opacity: 0.25,
                        width: { xs: '120px', sm: '150px', md: '180px' },
                        height: { xs: '67px', sm: '83px', md: '100px' }
                    }}
                >
                    <path
                        d="M 0 50 Q 45 10 90 50 T 180 50"
                        stroke="url(#bluePurple4)"
                        strokeWidth="0.8"
                        fill="none"
                        strokeLinecap="round"
                        strokeDasharray="1,2"
                    />
                    <path
                        d="M 0 50 Q 45 10 90 50 T 180 50"
                        stroke="url(#bluePurple4)"
                        strokeWidth="2.2"
                        fill="none"
                        strokeLinecap="round"
                        opacity="0.5"
                    />
                    <defs>
                        <linearGradient id="bluePurple4" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#0ea5e9" />
                            <stop offset="50%" stopColor="#8b5cf6" />
                            <stop offset="100%" stopColor="#ec4899" />
                        </linearGradient>
                    </defs>
                </svg>

                {/* Top far right wavy line */}
                <svg
                    width="120"
                    height="80"
                    viewBox="0 0 120 80"
                    style={{
                        position: 'absolute',
                        top: { xs: '70px', md: '90px' },
                        right: { xs: '40px', md: '80px' },
                        opacity: 0.2,
                        width: { xs: '80px', sm: '100px', md: '120px' },
                        height: { xs: '53px', sm: '67px', md: '80px' }
                    }}
                >
                    <path
                        d="M 0 40 Q 30 20 60 40 T 120 40"
                        stroke="url(#bluePurple5)"
                        strokeWidth="0.6"
                        fill="none"
                        strokeLinecap="round"
                    />
                    <path
                        d="M 0 40 Q 30 20 60 40 T 120 40"
                        stroke="url(#bluePurple5)"
                        strokeWidth="1.8"
                        fill="none"
                        strokeLinecap="round"
                        opacity="0.4"
                    />
                    <defs>
                        <linearGradient id="bluePurple5" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#0284c7" />
                            <stop offset="50%" stopColor="#7c3aed" />
                            <stop offset="100%" stopColor="#be185d" />
                        </linearGradient>
                    </defs>
                </svg>

                {/* Floating diagonal line */}
                <svg
                    width="150"
                    height="150"
                    viewBox="0 0 150 150"
                    style={{
                        position: 'absolute',
                        top: { xs: '120px', md: '150px' },
                        left: { xs: '120px', md: '200px' },
                        opacity: 0.15,
                        width: { xs: '100px', sm: '125px', md: '150px' },
                        height: { xs: '100px', sm: '125px', md: '150px' }
                    }}
                >
                    <path
                        d="M 0 75 Q 37.5 25 75 75 T 150 75"
                        stroke="url(#bluePurple6)"
                        strokeWidth="0.5"
                        fill="none"
                        strokeLinecap="round"
                        strokeDasharray="3,2"
                    />
                    <path
                        d="M 0 75 Q 37.5 25 75 75 T 150 75"
                        stroke="url(#bluePurple6)"
                        strokeWidth="1.5"
                        fill="none"
                        strokeLinecap="round"
                        opacity="0.3"
                    />
                    <defs>
                        <linearGradient id="bluePurple6" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#0c4a6e" />
                            <stop offset="50%" stopColor="#6366f1" />
                            <stop offset="100%" stopColor="#a855f7" />
                        </linearGradient>
                    </defs>
                </svg>
            </Box>

            {/* Header/Navigation */}
            <AppBar position="static" sx={{ backgroundColor: 'transparent', color: 'white', boxShadow: 'none', mt: 2, position: 'relative', zIndex: 1 }}>
                <Toolbar sx={{ justifyContent: 'space-between', alignItems: 'center', flexDirection: 'column' }}>
                    {/* Top Row: Logo and Menu Button */}
                    <Box sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                        mb: { xs: 1, md: 0 }
                    }}>
                        {/* Logo */}
                        <Box sx={{ 
                          width: { xs: 100, md: 120 }, 
                          height: { xs: 35, md: 40 }, 
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: { xs: 1, md: 0 },
                          overflow: 'hidden'
                        }}>
                          <img 
                            src={require('./axentric_logo_icon.png')} 
                            alt="Axentric Logo" 
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain'
                            }}
                          />
                        </Box>

                        {/* Desktop Navigation - hidden on mobile */}
                        <Box sx={{ 
                          display: { xs: 'none', md: 'flex' }, 
                          gap: 3, 
                          position: 'absolute',
                          left: '50%',
                          transform: 'translateX(-50%)'
                        }}>
                          <Button color="inherit">Home</Button>
                          <Button color="inherit">About</Button>
                          <Button color="inherit">Contact</Button>
                        </Box>

                        {/* Mobile Menu Button - visible only on mobile */}
                        <IconButton
                            color="inherit"
                            aria-label="toggle mobile menu"
                            onClick={handleDrawerToggle}
                            sx={{
                                display: { xs: 'flex', md: 'none' },
                                color: 'white'
                            }}
                        >
                            <MenuIcon />
                        </IconButton>

                        {/* Desktop Spacer - hidden on mobile */}
                        <Box sx={{ width: { xs: 0, md: '120px' } }} />
                    </Box>

                    {/* Mobile Navigation - expands vertically in header */}
                    <Box sx={{
                        display: { xs: 'block', md: 'none' },
                        width: '100%',
                        overflow: 'hidden',
                        transition: 'all 0.3s ease',
                        maxHeight: mobileOpen ? '300px' : '0px',
                        opacity: mobileOpen ? 1 : 0
                    }}>
                        <Box sx={{ 
                          py: 2,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1,
                          borderTop: '1px solid rgba(255,255,255,0.2)'
                        }}>
                          {['Home', 'About', 'Contact'].map((text) => (
                            <Button
                              key={text}
                              color="inherit"
                              sx={{
                                justifyContent: 'flex-start',
                                px: 2,
                                py: 1.5,
                                color: 'white',
                                '&:hover': {
                                  backgroundColor: 'rgba(255,255,255,0.1)'
                                }
                              }}
                            >
                              {text}
                            </Button>
                          ))}
                        </Box>
                    </Box>
                </Toolbar>
            </AppBar>

            {/* Hero Section */}
            <Box sx={{
                color: 'white',
                py: 8,
                textAlign: 'center',
                position: 'relative',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '300px', sm: '450px', md: '600px' },
                    height: { xs: '200px', sm: '300px', md: '400px' },
                    background: 'radial-gradient(ellipse, rgba(59, 130, 246, 0.03) 0%, rgba(139, 92, 246, 0.02) 50%, transparent 100%)',
                    borderRadius: '50%',
                    filter: 'blur(40px)',
                    zIndex: 0
                }
            }}>
                {/* Abstract Blue & Purple Lines - Hero Area */}
                <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    pointerEvents: 'none',
                    zIndex: 0
                }}>
                    {/* Left side curved line */}
                    <svg
                        width="180"
                        height="300"
                        viewBox="0 0 180 300"
                        style={{
                            position: 'absolute',
                            top: { xs: '30px', md: '50px' },
                            left: { xs: '15px', md: '30px' },
                            opacity: 0.3,
                            width: { xs: '120px', sm: '150px', md: '180px' },
                            height: { xs: '200px', sm: '250px', md: '300px' }
                        }}
                    >
                        <path
                            d="M 90 0 Q 45 75 90 150 T 90 300"
                            stroke="url(#bluePurple7)"
                            strokeWidth="0.8"
                            fill="none"
                            strokeLinecap="round"
                        />
                        <path
                            d="M 90 0 Q 45 75 90 150 T 90 300"
                            stroke="url(#bluePurple7)"
                            strokeWidth="2.5"
                            fill="none"
                            strokeLinecap="round"
                            opacity="0.6"
                        />
                        <defs>
                            <linearGradient id="bluePurple7" x1="0%" y1="0%" x2="0%" y2="100%">
                                <stop offset="0%" stopColor="#1e40af" />
                                <stop offset="30%" stopColor="#3b82f6" />
                                <stop offset="60%" stopColor="#8b5cf6" />
                                <stop offset="100%" stopColor="#a855f7" />
                            </linearGradient>
                        </defs>
                    </svg>

                    {/* Right side curved line */}
                    <svg
                        width="200"
                        height="280"
                        viewBox="0 0 200 280"
                        style={{
                            position: 'absolute',
                            top: { xs: '40px', md: '60px' },
                            right: { xs: '20px', md: '40px' },
                            opacity: 0.25,
                            width: { xs: '130px', sm: '165px', md: '200px' },
                            height: { xs: '187px', sm: '234px', md: '280px' }
                        }}
                    >
                        <path
                            d="M 100 0 Q 150 70 100 140 T 100 280"
                            stroke="url(#bluePurple8)"
                            strokeWidth="0.6"
                            fill="none"
                            strokeLinecap="round"
                        />
                        <path
                            d="M 100 0 Q 150 70 100 140 T 100 280"
                            stroke="url(#bluePurple8)"
                            strokeWidth="2.2"
                            fill="none"
                            strokeLinecap="round"
                            opacity="0.5"
                        />
                        <defs>
                            <linearGradient id="bluePurple8" x1="0%" y1="0%" x2="0%" y2="100%">
                                <stop offset="0%" stopColor="#0ea5e9" />
                                <stop offset="40%" stopColor="#6366f1" />
                                <stop offset="80%" stopColor="#a855f7" />
                                <stop offset="100%" stopColor="#7c3aed" />
                            </linearGradient>
                        </defs>
                    </svg>

                    {/* Bottom center curved line */}
                    <svg
                        width="400"
                        height="100"
                        viewBox="0 0 400 100"
                        style={{
                            position: 'absolute',
                            bottom: { xs: '15px', md: '20px' },
                            left: '50%',
                            transform: 'translateX(-50%)',
                            opacity: 0.2,
                            width: { xs: '250px', sm: '325px', md: '400px' },
                            height: { xs: '63px', sm: '81px', md: '100px' }
                        }}
                    >
                        <path
                            d="M 0 50 Q 100 20 200 50 T 400 50"
                            stroke="url(#bluePurple9)"
                            strokeWidth="0.8"
                            fill="none"
                            strokeLinecap="round"
                        />
                        <path
                            d="M 0 50 Q 100 20 200 50 T 400 50"
                            stroke="url(#bluePurple9)"
                            strokeWidth="2.8"
                            fill="none"
                            strokeLinecap="round"
                            opacity="0.5"
                        />
                        <defs>
                            <linearGradient id="bluePurple9" x1="0%" y1="0%" x2="100%" y2="0%">
                                <stop offset="0%" stopColor="#1d4ed8" />
                                <stop offset="25%" stopColor="#6366f1" />
                                <stop offset="50%" stopColor="#8b5cf6" />
                                <stop offset="75%" stopColor="#a855f7" />
                                <stop offset="100%" stopColor="#7c3aed" />
                            </linearGradient>
                        </defs>
                    </svg>

                    {/* Additional playful lines scattered around */}
                    <svg
                        width="120"
                        height="120"
                        viewBox="0 0 120 120"
                        style={{
                            position: 'absolute',
                            top: { xs: '90px', md: '120px' },
                            left: { xs: '120px', md: '180px' },
                            opacity: 0.2,
                            width: { xs: '80px', sm: '100px', md: '120px' },
                            height: { xs: '80px', sm: '100px', md: '120px' }
                        }}
                    >
                        <path
                            d="M 0 60 Q 30 30 60 60 T 120 60"
                            stroke="url(#bluePurple10)"
                            strokeWidth="0.5"
                            fill="none"
                            strokeLinecap="round"
                            strokeDasharray="2,3"
                        />
                        <path
                            d="M 0 60 Q 30 30 60 60 T 120 60"
                            stroke="url(#bluePurple10)"
                            strokeWidth="1.8"
                            fill="none"
                            strokeLinecap="round"
                            opacity="0.4"
                        />
                        <defs>
                            <linearGradient id="bluePurple10" x1="0%" y1="0%" x2="100%" y2="0%">
                                <stop offset="0%" stopColor="#0c4a6e" />
                                <stop offset="50%" stopColor="#3b82f6" />
                                <stop offset="100%" stopColor="#8b5cf6" />
                            </linearGradient>
                        </defs>
                    </svg>

                    {/* Floating wavy line */}
                    <svg
                        width="100"
                        height="80"
                        viewBox="0 0 100 80"
                        style={{
                            position: 'absolute',
                            top: '200px',
                            right: '120px',
                            opacity: 0.15
                        }}
                    >
                        <path
                            d="M 0 40 Q 25 20 50 40 T 100 40"
                            stroke="url(#bluePurple11)"
                            strokeWidth="0.6"
                            fill="none"
                            strokeLinecap="round"
                        />
                        <path
                            d="M 0 40 Q 25 20 50 40 T 100 40"
                            stroke="url(#bluePurple11)"
                            strokeWidth="1.5"
                            fill="none"
                            strokeLinecap="round"
                            opacity="0.3"
                        />
                        <defs>
                            <linearGradient id="bluePurple11" x1="0%" y1="0%" x2="100%" y2="0%">
                                <stop offset="0%" stopColor="#0284c7" />
                                <stop offset="50%" stopColor="#7c3aed" />
                                <stop offset="100%" stopColor="#be185d" />
                            </linearGradient>
                        </defs>
                    </svg>

                    {/* Diagonal accent line */}
                    <svg
                        width="140"
                        height="140"
                        viewBox="0 0 140 140"
                        style={{
                            position: 'absolute',
                            top: '80px',
                            left: '350px',
                            opacity: 0.18
                        }}
                    >
                        <path
                            d="M 0 70 Q 35 35 70 70 T 140 70"
                            stroke="url(#bluePurple12)"
                            strokeWidth="0.7"
                            fill="none"
                            strokeLinecap="round"
                            strokeDasharray="1,4"
                        />
                        <path
                            d="M 0 70 Q 35 35 70 70 T 140 70"
                            stroke="url(#bluePurple12)"
                            strokeWidth="2"
                            fill="none"
                            strokeLinecap="round"
                            opacity="0.4"
                        />
                        <defs>
                            <linearGradient id="bluePurple12" x1="0%" y1="0%" x2="100%" y2="0%">
                                <stop offset="0%" stopColor="#1e3a8a" />
                                <stop offset="50%" stopColor="#6366f1" />
                                <stop offset="100%" stopColor="#a855f7" />
                            </linearGradient>
                        </defs>
                    </svg>
                </Box>

                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                    <Grid container spacing={4} alignItems="center">
                        {/* Image Placeholder - Above headline on mobile, right side on desktop */}
                        <Grid item xs={12} md={6} sx={{ order: { xs: 1, md: 2 } }}>
                            <Box sx={{
                                width: '100%',
                                height: { xs: 250, md: 400 },
                                borderRadius: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mb: { xs: 3, md: 0 },
                                overflow: 'hidden'
                            }}>
                                <img 
                                  src={require('./images/human-robot.png')} 
                                  alt="Human and Robot collaboration" 
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain',
                                    borderRadius: '8px'
                                  }}
                                />
                            </Box>
                        </Grid>

                        {/* Content - Below image on mobile, left side on desktop */}
                        <Grid item xs={12} md={6} sx={{ order: { xs: 2, md: 1 } }}>
                            <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                                <Typography variant="h2" component="h1" sx={{
                                    mb: 3,
                                    fontWeight: 'bold',
                                    fontSize: { xs: '2.8rem', sm: '2.8rem', md: '3rem' }
                                }}>
                                    Your business. Your solution. Your AI agents.
                                </Typography>
                                <Typography variant="h6" sx={{ mb: 4, opacity: 0.9, lineHeight: 1.6 }}>
                                    Axentric is a platform for fast delivery of unique business solutions operated by AI agents that are adaptive, reliable, and designed to work in tandem with humans.
                                </Typography>
                                <Box sx={{
                                    display: 'flex',
                                    gap: 2,
                                    flexWrap: 'wrap',
                                    justifyContent: { xs: 'center', md: 'flex-start' }
                                }}>
                                    <Button
                                        variant="contained"
                                        size="large"
                                        sx={{
                                            backgroundColor: 'black',
                                            '&:hover': { backgroundColor: '#333' }
                                        }}
                                    >
                                        Contact us
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="large"
                                        sx={{
                                            borderColor: 'white',
                                            color: 'white',
                                            '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.1)' }
                                        }}
                                    >
                                        Learn more
                                    </Button>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Healthcare Solutions Section */}
            <Box sx={{ 
                py: 8, 
                position: 'relative',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: '10%',
                    right: '5%',
                    width: '300px',
                    height: '300px',
                    background: 'radial-gradient(circle, rgba(59, 130, 246, 0.08) 0%, rgba(139, 92, 246, 0.04) 50%, transparent 100%)',
                    borderRadius: '50%',
                    filter: 'blur(40px)',
                    zIndex: 0,
                    display: { xs: 'none', md: 'block' }
                }
            }}>
                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                    <Typography variant="h5" sx={{ mb: 4, textAlign: 'center', maxWidth: 800, mx: 'auto', color: 'white' }}>
                        Axentric delivers agentic solutions for enterprise AI transformation through deeply personalized products and work automation.
                    </Typography>

                    <Card sx={{ 
                        maxWidth: { xs: 400, md: 650 },
                        mx: 'auto', 
                        mt: 6,
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), 0 4px 16px rgba(0, 0, 0, 0.2)',
                        borderRadius: 4,
                        overflow: 'hidden',
                        background: 'linear-gradient(135deg, #0f1418 0%, #1a1f25 20%, #1e252b 40%, #1a1f25 60%, #0f1418 80%, #0a0e10 100%)',
                        border: '2.5px solid #1e252b',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.4), 0 8px 24px rgba(0, 0, 0, 0.3)'
                        }
                    }}>
                        {/* Content Section - Above Image */}
                        <Box sx={{ p: 4, pb: 2 }}>
                            <Grid container spacing={3} alignItems="flex-start">
                                {/* Text Content */}
                                <Grid item xs={12} md={8}>
                                    <Typography variant="h4" sx={{ 
                                    mb: 2, 
                                    fontWeight: 'bold', 
                                    color: 'white',
                                    fontSize: { xs: '1.5rem', md: '2rem' }
                                }}>
                                        Axentric in Healthcare administration
                                    </Typography>
                                </Grid>
                                
                                {/* Learn More Button */}
                                <Grid item xs={12} md={4}>
                                    <Box sx={{ 
                                        display: 'flex', 
                                        justifyContent: { xs: 'flex-start', md: 'flex-end' },
                                        pt: { xs: 0, md: 0 }
                                    }}>
                                        <Button 
                                            variant="outlined" 
                                            size="large"
                                            component="a"
                                            href="/healthcare"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{
                                                px: { xs: 2.5, md: 4 },
                                                py: { xs: 1, md: 1.5 },
                                                borderColor: 'rgba(255, 255, 255, 0.3)',
                                                color: 'white',
                                                borderRadius: 2,
                                                fontWeight: 500,
                                                textTransform: 'none',
                                                fontSize: { xs: '0.9rem', md: '1rem' },
                                                transition: 'all 0.3s ease',
                                                '&:hover': {
                                                    borderColor: 'white',
                                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                                                }
                                            }}
                                        >
                                            Learn more
                                        </Button>
                                    </Box>
                                </Grid>
                                
                                {/* Description Text - Full Width */}
                                <Grid item xs={12}>
                                    <Typography variant="body1" sx={{ 
                                        lineHeight: 1.7,
                                        fontSize: { xs: '1rem', md: '1.1rem' },
                                        color: 'white'
                                    }}>
                                        Admins spend 90% of their time on emails, offers, scheduling, and insurance. Axentric's AI agents team up with them to automate these tasks.
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Box>
                        
                        {/* Image Section - Lower Right Corner, No Right/Bottom Margins */}
                        <Box sx={{ 
                          width: '100%',
                          height: { xs: 300, md: 400 },
                          position: 'relative',
                          pr: 4,
                          pl: 0,
                          pb: 0
                        }}>
                          <Box sx={{ 
                            width: { xs: 400, md: 600 },
                            height: '100%',
                            borderRadius: 3,
                            overflow: 'hidden',
                            marginLeft: 'auto',
                            backgroundColor: '#0a0e10'
                          }}>
                            <img 
                              src={require('./images/financial_offer_creation.png')} 
                              alt="Financial Offer Creation with AI" 
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                                borderRadius: '12px'
                              }}
                            />
                          </Box>
                        </Box>
                    </Card>
                </Container>
            </Box>

            {/* Feature Heading Section */}
            <Box sx={{ 
                py: 8, 
                position: 'relative',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: '20%',
                    left: '8%',
                    width: '250px',
                    height: '250px',
                    background: 'radial-gradient(circle, rgba(139, 92, 246, 0.06) 0%, rgba(168, 85, 247, 0.03) 50%, transparent 100%)',
                    borderRadius: '50%',
                    filter: 'blur(35px)',
                    zIndex: 0,
                    display: { xs: 'none', md: 'block' }
                },
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: '15%',
                    right: '10%',
                    width: '200px',
                    height: '200px',
                    background: 'radial-gradient(circle, rgba(59, 130, 246, 0.07) 0%, rgba(30, 64, 175, 0.04) 50%, transparent 100%)',
                    borderRadius: '50%',
                    filter: 'blur(30px)',
                    zIndex: 0,
                    display: { xs: 'none', md: 'block' }
                }
            }}>
                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                    <Grid container spacing={6} alignItems="center">
                        <Grid item xs={12} md={5}>
                            <Typography
                                variant="h2"
                                sx={{
                                    mb: { xs: 2, md: 4 },
                                    textAlign: { xs: 'center', md: 'left' },
                                    fontSize: { xs: '2rem', md: '3rem' },
                                    fontWeight: { xs: '900', md: '400' },
                                    color: 'white'
                                }}
                            >
                                Your business in the future
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    mb: { xs: 2, md: 4 },
                                    fontSize: { xs: '1.1rem', md: '1.4rem' },
                                    lineHeight: 1.6,
                                    textAlign: { xs: 'center', md: 'left' },
                                    color: 'white'
                                }}
                            >
                                Axentric reveals the parts of your business where AI can work better and faster with less human effort and delivers high-impact outcomes through full-stack AI solutions.
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={6} md={6}>
                                    <Typography
                                        variant="h2"
                                        sx={{
                                            color: 'white',
                                            fontWeight: 'bold',
                                            textAlign: { xs: 'center', md: 'left' },
                                            fontSize: { xs: '2rem', md: '3rem' },
                                            mb: { xs: 0.5, md: 1 }
                                        }}
                                    >
                                        80%
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            textAlign: { xs: 'center', md: 'left' },
                                            color: 'white',
                                            fontSize: { xs: '0.9rem', md: '1.2rem' }
                                        }}
                                    >
                                        Less manual work
                                    </Typography>
                                </Grid>
                                <Grid item xs={6} md={6}>
                                    <Typography
                                        variant="h2"
                                        sx={{
                                            color: 'white',
                                            fontWeight: 'bold',
                                            textAlign: { xs: 'center', md: 'left' },
                                            fontSize: { xs: '2rem', md: '3rem' },
                                            mb: { xs: 0.5, md: 1 }
                                        }}
                                    >
                                        +10x
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            textAlign: { xs: 'center', md: 'left' },
                                            color: 'white',
                                            fontSize: { xs: '0.9rem', md: '1.2rem' }
                                        }}
                                    >
                                        Faster process completion
                                    </Typography>
                                </Grid>
                                <Grid item xs={6} md={6}>
                                    <Typography
                                        variant="h2"
                                        sx={{
                                            color: 'white',
                                            fontWeight: 'bold',
                                            textAlign: { xs: 'center', md: 'left' },
                                            fontSize: { xs: '2rem', md: '3rem' },
                                            mb: { xs: 0.5, md: 1 }
                                        }}
                                    >
                                        24/7
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            textAlign: { xs: 'center', md: 'left' },
                                            color: 'white',
                                            fontSize: { xs: '0.9rem', md: '1.2rem' }
                                        }}
                                    >
                                        AI agent availability
                                    </Typography>
                                </Grid>
                                <Grid item xs={6} md={6}>
                                    <Typography
                                        variant="h2"
                                        sx={{
                                            color: 'white',
                                            fontWeight: 'bold',
                                            textAlign: { xs: 'center', md: 'left' },
                                            fontSize: { xs: '2rem', md: '3rem' },
                                            mb: { xs: 0.5, md: 1 }
                                        }}
                                    >
                                        100%
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            textAlign: { xs: 'center', md: 'left' },
                                            color: 'white',
                                            fontSize: { xs: '0.9rem', md: '1.2rem' }
                                        }}
                                    >
                                        Reduction of human error
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={12} md={7}>
                            <Box sx={{ 
                                width: '100%', 
                                height: { xs: 400, md: 500 }, 
                                borderRadius: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden',
                                mt: { xs: 2, md: 0 }
                            }}>
                                <img 
                                    src={require('./images/full_stack_ai.png')} 
                                    alt="Full Stack AI Solutions" 
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'contain',
                                        borderRadius: '8px'
                                    }}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Reliability and Observability Section */}
            <Box sx={{ 
                py: 8, 
                position: 'relative',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: '30%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '400px',
                    height: '200px',
                    background: 'linear-gradient(90deg, rgba(59, 130, 246, 0.05) 0%, rgba(139, 92, 246, 0.08) 50%, rgba(59, 130, 246, 0.05) 100%)',
                    borderRadius: '50%',
                    filter: 'blur(50px)',
                    zIndex: 0,
                    display: { xs: 'none', md: 'block' }
                }
            }}>
                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                    {/* First Section: 100% reliability and observability */}
                    <Box sx={{ mb: 8 }}>
                        <Grid container spacing={6} alignItems="center">
                            <Grid item xs={12} md={6}>
                                <Box sx={{ 
                                    p: 4, 
                                    borderRadius: 3,
                                    background: 'linear-gradient(135deg, rgba(30, 37, 43, 0.3) 0%, rgba(26, 31, 37, 0.3) 100%)',
                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                    position: 'relative',
                                    overflow: 'hidden'
                                }}>
                                    <Box sx={{ 
                                        position: 'absolute', 
                                        top: 0, 
                                        left: 0, 
                                        width: '4px', 
                                        height: '100%', 
                                        background: 'linear-gradient(180deg, #1e252b 0%, #0f1418 100%)',
                                        borderRadius: '0 2px 2px 0'
                                    }} />
                                    <Typography variant="h4" sx={{ 
                                        mb: 3, 
                                        color: 'white', 
                                        fontWeight: { xs: '700', md: '600' },
                                        fontSize: { xs: '1.5rem', md: '2rem' }
                                    }}>
                                        100% reliability and observability with humans in the loop
                                    </Typography>
                                    <Typography variant="body1" sx={{ 
                                        color: 'rgba(255, 255, 255, 0.9)', 
                                        lineHeight: 1.7,
                                        fontSize: '1.1rem'
                                    }}>
                                        AI agents observe, react, listen and take over the work in collaboration with humans. Humans approve, AI does the heavy lifting.
                                    </Typography>
                                </Box>
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                                <Box sx={{ 
                                    p: 4, 
                                    borderRadius: 3,
                                    background: 'linear-gradient(135deg, rgba(30, 37, 43, 0.3) 0%, rgba(26, 31, 37, 0.3) 100%)',
                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                    position: 'relative',
                                    overflow: 'hidden'
                                }}>
                                    <Box sx={{ 
                                        position: 'absolute', 
                                        top: 0, 
                                        left: 0, 
                                        width: '4px', 
                                        height: '100%', 
                                        background: 'linear-gradient(180deg, #1e252b 0%, #0f1418 100%)',
                                        borderRadius: '0 2px 2px 0'
                                    }} />
                                    <Typography variant="h4" sx={{ 
                                        mb: 3, 
                                        color: 'white', 
                                        fontWeight: { xs: '700', md: '600' },
                                        fontSize: { xs: '1.5rem', md: '2rem' }
                                    }}>
                                        Continuous improvement with human interaction
                                    </Typography>
                                    <Typography variant="body1" sx={{ 
                                        color: 'rgba(255, 255, 255, 0.9)', 
                                        lineHeight: 1.7,
                                        fontSize: '1.1rem'
                                    }}>
                                        The longer you use your solution, the better it gets. AI agents learn from your employees and improve with time.
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                </Container>
            </Box>

            {/* Customer Journey Section */}
            <Box sx={{ 
                py: 8, 
                position: 'relative',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: '15%',
                    left: '15%',
                    width: '180px',
                    height: '180px',
                    background: 'radial-gradient(circle, rgba(139, 92, 246, 0.06) 0%, rgba(168, 85, 247, 0.03) 50%, transparent 100%)',
                    borderRadius: '50%',
                    filter: 'blur(25px)',
                    zIndex: 0,
                    display: { xs: 'none', md: 'block' }
                },
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: '25%',
                    right: '20%',
                    width: '220px',
                    height: '220px',
                    background: 'radial-gradient(circle, rgba(59, 130, 246, 0.05) 0%, rgba(30, 64, 175, 0.03) 50%, transparent 100%)',
                    borderRadius: '50%',
                    filter: 'blur(30px)',
                    zIndex: 0,
                    display: { xs: 'none', md: 'block' }
                }
            }}>
                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                    <Typography variant="h5" sx={{ mb: 4, textAlign: 'center', maxWidth: 800, mx: 'auto', color: 'white' }}>
                        Uniqueness at scale. Axentric platform makes it possible to rapidly deliver outcomes for each client individually.
                    </Typography>
                    <Box sx={{ 
                        width: '100%', 
                        height: 600, 
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden'
                    }}>
                        <img 
                          src={require('./images/AI_engine.png')} 
                          alt="AI Engine Platform" 
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            borderRadius: '8px'
                          }}
                        />
                    </Box>
                </Container>
            </Box>

            {/* Contact Section */}
            <Box sx={{ 
                py: 8, 
                position: 'relative',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: '10%',
                    left: '10%',
                    width: '300px',
                    height: '300px',
                    background: 'radial-gradient(circle, rgba(59, 130, 246, 0.04) 0%, rgba(139, 92, 246, 0.06) 50%, transparent 100%)',
                    borderRadius: '50%',
                    filter: 'blur(45px)',
                    zIndex: 0,
                    display: { xs: 'none', md: 'block' }
                },
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: '10%',
                    right: '15%',
                    width: '250px',
                    height: '250px',
                    background: 'radial-gradient(circle, rgba(168, 85, 247, 0.05) 0%, rgba(139, 92, 246, 0.03) 50%, transparent 100%)',
                    borderRadius: '50%',
                    filter: 'blur(35px)',
                    zIndex: 0,
                    display: { xs: 'none', md: 'block' }
                }
            }}>
                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                    <Typography variant="h3" sx={{ mb: 6, textAlign: 'center', color: 'white' }}>
                        Let's make your business better together
                    </Typography>
                    
                    <Typography variant="body1" sx={{ 
                        mb: 6, 
                        textAlign: 'center', 
                        maxWidth: 900, 
                        mx: 'auto', 
                        color: 'rgba(255, 255, 255, 0.9)',
                        lineHeight: 1.7,
                        fontSize: '1.1rem'
                    }}>
                        Axentric platform can power up any industry or type of business with digital footprint by individually adapting already developed code components and AI agents. Along with recently developed Healthcare suite, we are expanding the reach further. So feel free to reach out and let's explore together.
                    </Typography>

                    <Grid container spacing={6}>
                        {/* Contact Information */}
                        <Grid item xs={12} md={4}>
                            <Box sx={{ mb: 4 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <PhoneIcon sx={{ mr: 2, color: '#1e252b' }} />
                                    <Typography variant="h6" sx={{ color: 'white' }}>Call us</Typography>
                                </Box>
                                <Typography variant="body1" sx={{ ml: 6, color: 'rgba(255, 255, 255, 0.9)' }}>+385919122577</Typography>
                            </Box>
                            
                            <Box sx={{ mb: 4 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <EmailIcon sx={{ mr: 2, color: '#1e252b' }} />
                                    <Typography variant="h6" sx={{ color: 'white' }}>Mail</Typography>
                                </Box>
                                <Typography variant="body1" sx={{ ml: 6, color: 'rgba(255, 255, 255, 0.9)' }}>info@axentric.ai</Typography>
                            </Box>
                        </Grid>

                        {/* Contact Form */}
                        <Grid item xs={12} md={8}>
                            <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Name"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            required
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': {
                                                        borderColor: 'rgba(255, 255, 255, 0.3)',
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: 'rgba(255, 255, 255, 0.5)',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: 'primary.main',
                                                    },
                                                },
                                                '& .MuiInputLabel-root': {
                                                    color: 'rgba(255, 255, 255, 0.7)',
                                                },
                                                '& .MuiInputBase-input': {
                                                    color: 'white',
                                                },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Phone number"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleInputChange}
                                            required
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': {
                                                        borderColor: 'rgba(255, 255, 255, 0.3)',
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: 'rgba(255, 255, 255, 0.5)',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: 'primary.main',
                                                    },
                                                },
                                                '& .MuiInputLabel-root': {
                                                    color: 'rgba(255, 255, 255, 0.7)',
                                                },
                                                '& .MuiInputBase-input': {
                                                    color: 'white',
                                                },
                                            }}
                                        />
                                    </Grid>
                                </Grid>

                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Email"
                                            name="email"
                                            type="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            required
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': {
                                                        borderColor: 'rgba(255, 255, 255, 0.3)',
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: 'rgba(255, 255, 255, 0.5)',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: 'primary.main',
                                                    },
                                                },
                                                '& .MuiInputLabel-root': {
                                                    color: 'rgba(255, 255, 255, 0.7)',
                                                },
                                                '& .MuiInputBase-input': {
                                                    color: 'white',
                                                },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Company"
                                            name="company"
                                            value={formData.company}
                                            onChange={handleInputChange}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': {
                                                        borderColor: 'rgba(255, 255, 255, 0.3)',
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: 'rgba(255, 255, 255, 0.5)',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: 'primary.main',
                                                    },
                                                },
                                                '& .MuiInputLabel-root': {
                                                    color: 'rgba(255, 255, 255, 0.7)',
                                                },
                                                '& .MuiInputBase-input': {
                                                    color: 'white',
                                                },
                                            }}
                                        />
                                    </Grid>
                                </Grid>

                                <TextField
                                    fullWidth
                                    label="Type your message"
                                    name="message"
                                    multiline
                                    rows={4}
                                    value={formData.message}
                                    onChange={handleInputChange}
                                    required
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: 'rgba(255, 255, 255, 0.3)',
                                            },
                                            '&:hover fieldset': {
                                                borderColor: 'rgba(255, 255, 255, 0.5)',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: 'primary.main',
                                            },
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: 'rgba(255, 255, 255, 0.7)',
                                        },
                                        '& .MuiInputBase-input': {
                                            color: 'white',
                                        },
                                    }}
                                />

                                <Button
                                    type="submit"
                                    variant="contained"
                                    size="large"
                                    startIcon={<CheckIcon />}
                                    sx={{
                                        backgroundColor: 'black',
                                        '&:hover': { backgroundColor: '#333' },
                                        alignSelf: 'flex-start'
                                    }}
                                >
                                    Submit
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Footer */}
            <Box sx={{ py: 4, color: 'white', textAlign: 'center' }}>
                <Typography variant="body2">
                    Copyright © 2025 Axentric. All rights reserved.
                </Typography>
            </Box>
        </Box>
    );
}

function App() {
    return (
        <Router>
            <Routes>
                <Route path="/" element={<MainContent />} />
                <Route path="/healthcare" element={<HealthcarePage onBack={() => window.history.back()} />} />
            </Routes>
        </Router>
    );
}

export default App;
